import { useMutation, useQuery, keepPreviousData } from "@tanstack/react-query";
import deviceService from "../lib/device.service";
import { deviceKeys } from "./use-devices";

export const logArchiveKeys = {
  list: (deviceId: string, from?: string, to?: string) =>
    [...deviceKeys.detail(deviceId), "logArchive", from ?? "", to ?? ""] as const,
};

/**
 * useLogArchive lists log objects discovered by scanning the configured
 * GCS archive buckets for a device. Distinct from useDeviceLogFiles, which
 * reads DB rows for files uploaded through the mdm path.
 *
 * Pass undefined for from/to to let the server default the range. The
 * server enforces a maximum range (max_range_days); attempting a wider
 * range returns 400 and the query lands in error state.
 */
export function useLogArchive(
  deviceId: string,
  from?: string,
  to?: string,
  enabled = true,
) {
  return useQuery({
    queryKey: logArchiveKeys.list(deviceId, from, to),
    queryFn: () => deviceService.listLogArchive(deviceId, from, to),
    enabled: enabled && !!deviceId,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false, // 400 (range too wide) shouldn't auto-retry
  });
}

/**
 * useLogArchiveDownload returns a signed download URL for a single
 * archive object. The mutation is a thin wrapper so callers can
 * `mutateAsync(...)` and immediately open the returned URL.
 */
export function useLogArchiveDownload(deviceId: string) {
  return useMutation({
    mutationFn: ({ bucket, name }: { bucket: string; name: string }) =>
      deviceService.getLogArchiveDownloadURL(deviceId, bucket, name),
  });
}
